import { useState, useCallback } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { TEST_S3_CONNECTION, IMPORT_FILE_FROM_S3 } from '../../../mutations/fileMutations';
import { FILES_QUERY } from '../../../queries';
import { SET_SUCCESS, SET_LOADING } from '../../../reducers/actionReducer';
import ErrorText from '../../ErrorText';
import LoadingSpinner from '../../LoadingSpinner';
import Button from '../../inputs/Button';
import CollapsibleInstructions from '../CollapsibleInstructions';

interface S3Config {
  sourceUrl: string;
  targetKey?: string;
}

interface S3ImportProps {
  dispatchAction: (action: any) => void;
}

export function S3Import({ dispatchAction }: S3ImportProps) {
  const [s3Config, setS3Config] = useState<S3Config>({
    sourceUrl: '',
    targetKey: '',
  });

  const [connectionTested, setConnectionTested] = useState(false);
  const [connectionValid, setConnectionValid] = useState(false);
  const [importing, setImporting] = useState(false);

  const apolloClient = useApolloClient();

  const [testConnection, { loading: testingConnection, error: connectionError }] =
    useMutation(TEST_S3_CONNECTION, { fetchPolicy: 'no-cache' });

  const [importFile, { loading: importingFile, error: importError }] =
    useMutation(IMPORT_FILE_FROM_S3, { fetchPolicy: 'no-cache' });

  const handleInputChange = useCallback(<K extends keyof S3Config>(field: K, value: S3Config[K]) => {
    setS3Config(prev => ({ ...prev, [field]: value }));
    if (connectionTested) {
      setConnectionTested(false);
      setConnectionValid(false);
    }
  }, [connectionTested]);

  const handleTestConnection = useCallback(async () => {
    try {
      const result = await testConnection({
        variables: {
          s3Config: {
            sourceUrl: s3Config.sourceUrl,
          },
        },
      });
      if (result.data?.testS3Connection?.success) {
        setConnectionTested(true);
        setConnectionValid(true);
      } else {
        setConnectionTested(true);
        setConnectionValid(false);
      }
    } catch {
      setConnectionTested(true);
      setConnectionValid(false);
    }
  }, [s3Config, testConnection]);

  const handleImportFile = useCallback(async () => {
    if (!connectionValid) return;

    setImporting(true);
    dispatchAction({
      type: SET_LOADING,
      payload: { message: 'Importing file from S3 URL...' },
    });

    try {
      const result = await importFile({
        variables: {
          s3Config: {
            sourceUrl: s3Config.sourceUrl,
            targetKey: s3Config.targetKey || undefined,
          },
        },
      });

      if (result.data?.importFileFromS3?.success) {
        dispatchAction({
          type: SET_SUCCESS,
          payload: { message: result.data.importFileFromS3.message },
        });
        apolloClient.refetchQueries({ include: [FILES_QUERY] });
      }
    } finally {
      setImporting(false);
    }
  }, [s3Config, connectionValid, importFile, dispatchAction, apolloClient]);

  const isFormValid = !!s3Config.sourceUrl;
  const canTestConnection = isFormValid && !testingConnection;
  const canImport = connectionValid && !importing && !importingFile;

  return (
    <div className="w-full flex flex-col gap-4 p-6">
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold mb-2">Import File from S3 (URL)</h3>
        <p className="text-gray-600 text-sm">Provide a public or presigned S3 URL</p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Source URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Source URL *</label>
          <input
            type="url"
            value={s3Config.sourceUrl}
            onChange={(e) => handleInputChange('sourceUrl', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="https://bucket.s3.region.amazonaws.com/path/file.csv?X-Amz-SignedHeaders=..."
          />
          <p className="mt-1 text-xs text-gray-500">
            Accepts public or presigned URL (GET).
          </p>
        </div>

        {/* Target Key (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target Key (optional)</label>
          <input
            type="text"
            value={s3Config.targetKey ?? ''}
            onChange={(e) => handleInputChange('targetKey', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            placeholder="folder/renamed-file.csv (leave empty to keep same name)"
          />
        </div>
      </div>

      {/* Connection Status */}
      {connectionTested && (
        <div className={`p-3 rounded-md ${connectionValid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <p className={`text-sm ${connectionValid ? 'text-green-700' : 'text-red-700'}`}>
            {connectionValid ? '✅ Connection successful!' : '❌ Connection failed'}
          </p>
        </div>
      )}

      {connectionError && <ErrorText message={connectionError.message} />}
      {importError && <ErrorText message={importError.message} />}

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="button" secondary onClick={handleTestConnection} disabled={!canTestConnection}>
          {testingConnection ? (<><LoadingSpinner className="w-4 h-4 mr-2" />Testing...</>) : ('Test Connection')}
        </Button>

        <Button type="button" primary onClick={handleImportFile} disabled={!canImport}>
          {importing || importingFile ? (<><LoadingSpinner className="w-4 h-4 mr-2" />Importing...</>) : ('Import File')}
        </Button>
      </div>

      <div className="text-xs text-gray-500 mt-2">* Required fields</div>

    <CollapsibleInstructions className="mt-6" />
    </div>
  );
}

export default S3Import;
